import { Fragment, useCallback, useState } from 'react';
import Table from 'shell/components/shared/table';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import { BaseTableRows, HeadCell } from 'shell/components/shared/table/types';
import { noop } from 'shell/utils/noop';
import ModalInsertParameterData from './parameter-modal';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { IconButton, Typography } from '@mui/material';
import { AddCircleOutline } from '@mui/icons-material';

export interface ParameterItemVM extends BaseTableRows {
  code?: string;
  name: string;
  description: string;
  value: string;
  schedulationType?: string;
  action?: string;
}

const headCells: HeadCell<ParameterItemVM, keyof ParameterItemVM>[] = [
  {
    id: 'name',
    label: 'Nome',
  },
  {
    id: 'description',
    label: 'Descrizione',
  },
  {
    id: 'value',
    label: 'Valore',
  },
  {
    id: 'action',
    label: '',
  },
];

interface ParameterTableProps {
  jobName?: string;
  body: Array<ParameterItemVM>;
  onConfirm: (values: Array<ParameterItemVM>) => void;
  disabled?: boolean;
}

const ParameterTable = ({
  body,
  jobName,
  onConfirm,
  disabled,
}: ParameterTableProps): React.ReactElement => {
  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<ParameterItemVM>>({
    element: null,
    open: false,
  });

  const [editModal, setEditModal] = useState<AlertModal<ParameterItemVM>>({
    element: null,
    open: false,
  });

  const [page, setPage] = useState<number>(0);

  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const onVisibilityPress = useCallback(
    (cell): Promise<ParameterItemVM> => {
      return new Promise((resolve) => {
        setEditModal({ element: cell, open: true, disabled: true });
        resolve(cell);
      });
    },
    [setEditModal]
  );

  const onEditPress = useCallback(
    (cell): Promise<ParameterItemVM> => {
      return new Promise((resolve) => {
        setEditModal({ element: cell, open: true });
        resolve(cell);
      });
    },
    [setEditModal]
  );

  const onTrashPress = useCallback(
    (cell): Promise<ParameterItemVM> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<ParameterItemVM>;
    },
    [setDeleteAlertModal]
  );

  const handleChangePage = useCallback(
    (newPage) => {
      setPage(newPage);
    },
    [setPage]
  );

  const handleChangeRowsPerPage = useCallback(
    (rows) => {
      setRowsPerPage(rows);
    },
    [setRowsPerPage]
  );

  const onAddBtnClick = useCallback(() => {
    setEditModal({ element: null, open: true });
  }, [setEditModal]);

  const onCloseDeleteAlertModal = useCallback(() => {
    setDeleteAlertModal({ open: false, element: null });
  }, [setDeleteAlertModal]);

  const onYesDeleteAlertModal = useCallback(() => {
    let slicedElements = body.slice();
    const values = { ...deleteAlertModal.element };

    if (slicedElements.find((item) => item.name === values.name)) {
      slicedElements = slicedElements.filter((item) => item.name !== values.name);

      onConfirm(slicedElements);
    }
    setDeleteAlertModal({ open: false, element: null });
  }, [setDeleteAlertModal, deleteAlertModal.element]);

  const onNoDeleteAlertModal = useCallback(() => {
    setDeleteAlertModal({ open: false, element: null });
  }, [setDeleteAlertModal]);

  return (
    <Fragment>
      <div className="pmi-d-flex pmi-justify-content-between">
        <PmiTitleDivider>Parametri</PmiTitleDivider>
        <span>
          <IconButton
            onClick={onAddBtnClick}
            aria-label="Aggiungi Parametro"
            aria-haspopup="true"
            disabled={disabled}
          >
            <AddCircleOutline />
          </IconButton>
          <Typography color="secondary" display="inline-block">
            Aggiungi Parametro
          </Typography>
        </span>
      </div>
      <Table
        head={headCells}
        onSort={noop}
        orders={{}}
        page={page}
        rowsPerPage={rowsPerPage}
        body={body}
        onVisibilityPress={onVisibilityPress}
        onEditPress={onEditPress}
        onTrashPress={onTrashPress}
        count={body.length}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        ariaLabel="Lista Parametri"
        noSort
      />
      <DeleteAlertModal
        title="Cancellazione Parametro"
        message="Confermare la cancellazione?"
        open={deleteAlertModal.open}
        onClose={onCloseDeleteAlertModal}
        onYes={onYesDeleteAlertModal}
        onNo={onNoDeleteAlertModal}
      />
      <ModalInsertParameterData
        jobName={jobName}
        open={editModal.open}
        modalData={editModal.element}
        disabled={editModal.disabled}
        onClose={() => {
          setEditModal({ element: null, open: false });
        }}
        onSubmit={(values) => {
          let slicedElements = body.slice();

          if (slicedElements.find((item) => item.name === values.name)) {
            slicedElements = slicedElements.map((item) =>
              item.name === values.name ? { ...item, ...values } : item
            );

            onConfirm(slicedElements);
            setEditModal({ element: null, open: false });
            return;
          }

          slicedElements.push(values);
          setEditModal({ element: null, open: false });
          onConfirm(slicedElements);
        }}
      />
    </Fragment>
  );
};

ParameterTable.displayName = 'Parameter List';

export default ParameterTable;
